import { useState, useEffect } from 'react';
import {
  Button,
  TextField,
  MenuItem,
  IconButton,
  Box,
  Typography,
  OutlinedInput,
  InputAdornment,
  FormControl,
  FormHelperText,
  Card,
  CardContent,
  Grid,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import * as yup from 'yup';
import { useForm, Controller } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import { toast } from 'react-hot-toast';
import Icon from 'src/@core/components/icon';
import { useNavigate } from 'react-router-dom';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import BlankLayout from 'src/@core/layouts/BlankLayout';
import { GuardWrapper } from 'src/layouts/GuardWrapper';
import axios from 'axios';

const schema = yup.object().shape({
  fullname: yup.string().required('Full name is required').min(3, 'Minimum 3 characters'),
  username: yup.string().required('Username is required').min(3, 'Minimum 3 characters'),
  email: yup.string().email('Invalid email').required('Email is required'),
  password: yup.string().min(6, 'Password must be at least 6 characters').required('Password is required'),
  confirmPassword: yup
    .string()
    .required('Please confirm your password')
    .oneOf([yup.ref('password')], 'Passwords do not match'),
  dateOfBirth: yup.date().typeError('Invalid date').required('Date of birth is required').nullable(),
  rank: yup.string().required('Rank is required'),
  country: yup.string().required('Country is required'),
  role: yup.string().required('Role is required'),
});

const defaultValues = {
  fullname: '',
  username: '',
  email: '',
  password: '',
  confirmPassword: '',
  dateOfBirth: null,
  rank: '',
  country: '',
  role: 'user',
};
const userData = JSON.parse(localStorage.getItem('userData') || '{}')
const subdomain = userData?.domain?.name || 'health'
const COUNTRIES = [
  'Pakistan',
  'United States',
  'United Kingdom',
  'India',
  'Canada',
];

const ROLES = [
  { label: 'User', value: 'user' },
  { label: 'Admin', value: 'admin' },
];

const RegisterPage = () => {
  const theme = useTheme();
  const navigate = useNavigate();
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [loading, setLoading] = useState(false);

  const {
    control,
    handleSubmit,
    formState: { errors },
    setValue,
    watch,
  } = useForm({
    defaultValues,
    mode: 'onBlur',
    resolver: yupResolver(schema),
  });

  // Super Admin Protection
  useEffect(() => {
    const userData = JSON.parse(localStorage.getItem('userData') || '{}');
    if (userData.isSuperAdmin !== true) {
      toast.error('Only Super Admins can access this page');
      navigate('/dashboard');
    }
  }, [navigate]);

  const onSubmit = async (data) => {
    setLoading(true);
    try {
      const token = localStorage.getItem('accessToken');
     

      // strip confirmPassword — backend doesn't need it
      const { confirmPassword, ...rest } = data;

      const payload = {
        ...rest,
        dateOfBirth: data.dateOfBirth
          ? new Date(data.dateOfBirth).toISOString().split('T')[0]
          : null,
      };

      await axios.post(`/api/jwt/register`, payload, {
        
        headers: {
          Authorization: `Bearer ${token}`,
          'x-subdomain': subdomain,
        },
      });

      toast.success('User registered successfully!');
      setTimeout(() => navigate('/dashboard'), 1500);
    } catch (err) {
      console.error(err.response?.data || err.message);
      toast.error(err.response?.data?.message || 'Registration failed');
    } finally {
      setLoading(false);
    }
  };

  return (
    <GuardWrapper>
      <Box
        className="content-right"
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          minHeight: '100vh',
          backgroundColor: theme.palette.background.default,
        }}
      >
        <Card sx={{ width: 480, borderRadius: 3, boxShadow: theme.shadows[4] }}>
          <CardContent sx={{ p: 6 }}>
            <Box display="flex" justifyContent="center" mb={4}>
              <img
                src="https://healthheroservices.com/wp-content/uploads/2023/04/logo-01.svg"
                alt="Logo"
                width={200}
              />
            </Box>

            <Typography variant="h5" textAlign="center" sx={{ mb: 1, fontWeight: 600 }}>
              Create New User
            </Typography>
            <Typography variant="body2" textAlign="center" sx={{ mb: 5, color: '#750f1f' }}>
              Super Admin Only
            </Typography>

            <form noValidate autoComplete="off" onSubmit={handleSubmit(onSubmit)}>
              <Grid container spacing={3}>

                {/* Full Name */}
                <Grid item xs={12}>
                  <Controller
                    name="fullname"
                    control={control}
                    render={({ field }) => (
                      <TextField
                        {...field}
                        label="Full Name"
                        fullWidth
                        error={Boolean(errors.fullname)}
                        helperText={errors.fullname?.message}
                      />
                    )}
                  />
                </Grid>

                {/* Username */}
                <Grid item xs={12}>
                  <Controller
                    name="username"
                    control={control}
                    render={({ field }) => (
                      <TextField
                        {...field}
                        label="Username"
                        fullWidth
                        error={Boolean(errors.username)}
                        helperText={errors.username?.message}
                      />
                    )}
                  />
                </Grid>

                {/* Email */}
                <Grid item xs={12}>
                  <Controller
                    name="email"
                    control={control}
                    render={({ field }) => (
                      <TextField
                        {...field}
                        label="Email"
                        fullWidth
                        error={Boolean(errors.email)}
                        helperText={errors.email?.message}
                      />
                    )}
                  />
                </Grid>

                {/* Password */}
                <Grid item xs={12}>
                  <Controller
                    name="password"
                    control={control}
                    render={({ field }) => (
                      <FormControl fullWidth error={Boolean(errors.password)}>
                        <OutlinedInput
                          {...field}
                          type={showPassword ? 'text' : 'password'}
                          placeholder="Create password"
                          endAdornment={
                            <InputAdornment position="end">
                              <IconButton
                                edge="end"
                                onClick={() => setShowPassword(!showPassword)}
                              >
                                <Icon icon={showPassword ? 'bx:show' : 'bx:hide'} />
                              </IconButton>
                            </InputAdornment>
                          }
                        />
                        {errors.password && (
                          <FormHelperText>{errors.password.message}</FormHelperText>
                        )}
                      </FormControl>
                    )}
                  />
                </Grid>

                {/* Confirm Password */}
                <Grid item xs={12}>
                  <Controller
                    name="confirmPassword"
                    control={control}
                    render={({ field }) => (
                      <FormControl fullWidth error={Boolean(errors.confirmPassword)}>
                        <OutlinedInput
                          {...field}
                          type={showConfirmPassword ? 'text' : 'password'}
                          placeholder="Confirm password"
                          endAdornment={
                            <InputAdornment position="end">
                              <IconButton
                                edge="end"
                                onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                              >
                                <Icon icon={showConfirmPassword ? 'bx:show' : 'bx:hide'} />
                              </IconButton>
                            </InputAdornment>
                          }
                        />
                        {errors.confirmPassword && (
                          <FormHelperText>{errors.confirmPassword.message}</FormHelperText>
                        )}
                      </FormControl>
                    )}
                  />
                </Grid>

                {/* Date of Birth */}
                <Grid item xs={12}>
                  <LocalizationProvider dateAdapter={AdapterDateFns}>
                    <Controller
                      name="dateOfBirth"
                      control={control}
                      render={() => (
                        <DatePicker
                          label="Date of Birth"
                          value={watch('dateOfBirth')}
                          onChange={(newValue) => setValue('dateOfBirth', newValue, { shouldValidate: true })}
                          slotProps={{
                            textField: {
                              fullWidth: true,
                              error: Boolean(errors.dateOfBirth),
                              helperText: errors.dateOfBirth?.message,
                            },
                          }}
                        />
                      )}
                    />
                  </LocalizationProvider>
                </Grid>

                {/* Rank */}
                <Grid item xs={12}>
                  <Controller
                    name="rank"
                    control={control}
                    render={({ field }) => (
                      <TextField
                        {...field}
                        label="Rank / Title"
                        placeholder="Doctor, Nurse, etc."
                        fullWidth
                        error={Boolean(errors.rank)}
                        helperText={errors.rank?.message}
                      />
                    )}
                  />
                </Grid>

                {/* Country */}
                <Grid item xs={12}>
                  <Controller
                    name="country"
                    control={control}
                    render={({ field }) => (
                      <TextField
                        {...field}
                        select
                        label="Country"
                        fullWidth
                        error={Boolean(errors.country)}
                        helperText={errors.country?.message}
                      >
                        {COUNTRIES.map((c) => (
                          <MenuItem key={c} value={c}>
                            {c}
                          </MenuItem>
                        ))}
                      </TextField>
                    )}
                  />
                </Grid>

                {/* Role */}
                <Grid item xs={12}>
                  <Controller
                    name="role"
                    control={control}
                    render={({ field }) => (
                      <TextField
                        {...field}
                        select
                        label="Role"
                        fullWidth
                        error={Boolean(errors.role)}
                        helperText={errors.role?.message}
                      >
                        {ROLES.map((r) => (
                          <MenuItem key={r.value} value={r.value}>
                            {r.label}
                          </MenuItem>
                        ))}
                      </TextField>
                    )}
                  />
                </Grid>

                {/* Submit */}
                <Grid item xs={12} sx={{ mt: 2 }}>
                  <Button
                    fullWidth
                    variant="contained"
                    size="large"
                    type="submit"
                    disabled={loading}
                    sx={{
                      backgroundColor: '#750f1f',
                      '&:hover': { backgroundColor: '#5e0c19' },
                      py: 2,
                    }}
                  >
                    {loading ? 'Creating Account...' : 'Create Account'}
                  </Button>
                </Grid>

              </Grid>
            </form>
          </CardContent>
        </Card>
      </Box>
    </GuardWrapper>
  );
};

RegisterPage.getLayout = (page) => <BlankLayout>{page}</BlankLayout>;

export default RegisterPage;